import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Plus, ExternalLink, Copy, Eye, EyeOff, Pencil, Trash2, Building2, KeyRound, LogIn, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { initials } from "@/lib/ui";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import CreatePortalDialog from "@/components/CreatePortalDialog";

function AuditDialog({ portal, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/portals/${portal.id}/audit`).then((r) => setRows(r.data)).catch(() => {}); }, [portal.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="portal-audit-dialog">
        <DialogHeader><DialogTitle className="font-display">Access log — {portal.label}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Who opened this portal and when.</p>
        <div className="max-h-80 overflow-y-auto scroll-thin divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="py-2.5 flex items-center gap-3" data-testid={`audit-row-${r.id}`}>
              <Avatar className="w-7 h-7"><AvatarFallback className="bg-slate-100 text-slate-600 text-[10px]">{initials(r.user_name)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{r.user_name} <span className="text-xs text-slate-400 font-normal">({r.user_role})</span></p>
                <p className="text-xs text-slate-400">opened this portal</p>
              </div>
              <span className="text-xs text-slate-400 shrink-0">{new Date(r.created_at).toLocaleString()}</span>
            </div>
          ))}
          {rows.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No access recorded yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Secret({ value, testid }) {
  const [show, setShow] = useState(false);
  if (!value) return <span className="text-slate-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-mono text-xs text-slate-700" data-testid={testid}>{show ? value : "••••••••"}</span>
      <button onClick={() => setShow((s) => !s)} className="text-slate-400 hover:text-slate-600">{show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
      <button onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} className="text-slate-400 hover:text-indigo-600"><Copy className="w-3.5 h-3.5" /></button>
    </span>
  );
}

export default function PortalsPage() {
  const { user } = useAuth();
  const isStaff = user?.role === "manager" || user?.role === "engineer";
  const isManager = user?.role === "manager";
  const [portals, setPortals] = useState([]);
  const [clients, setClients] = useState([]);
  const [dialog, setDialog] = useState(null); // {existing} or {}
  const [audit, setAudit] = useState(null);

  const load = async () => {
    const { data } = await api.get("/portals");
    setPortals(data);
  };
  useEffect(() => {
    load();
    if (user?.role === "manager" || user?.role === "engineer") {
      api.get("/users", { params: { role: "client" } }).then((r) => setClients(r.data)).catch(() => {});
    }
  }, []);

  const grouped = useMemo(() => {
    const m = {};
    portals.forEach((p) => { (m[p.client_name] = m[p.client_name] || []).push(p); });
    return m;
  }, [portals]);

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.label}"?`)) return;
    await api.delete(`/portals/${p.id}`);
    toast.success("Portal removed"); load();
  };

  const openPortal = (p) => {
    const creds = `${p.username || ""}\t${p.password || ""}`.trim();
    if (creds && navigator.clipboard) navigator.clipboard.writeText(creds).catch(() => {});
    api.post(`/portals/${p.id}/access`).catch(() => {});
    toast.success("Credentials copied — opening portal");
    window.open(p.url, "_blank", "noopener");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]" data-testid="portals-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Client Portals</h1>
          <p className="text-sm text-slate-500 mt-1">Delivered pilot portals, URLs and access credentials for each client.</p>
        </div>
        {isStaff && (
          <Button onClick={() => setDialog({})} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-portal-button">
            <Plus className="w-4 h-4 mr-1.5" /> Add portal
          </Button>
        )}
      </div>

      <div className="space-y-6">
        {Object.entries(grouped).map(([clientName, items]) => (
          <div key={clientName} data-testid={`portal-group-${clientName}`}>
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-indigo-600" />
              <h2 className="font-display text-base font-semibold text-slate-800">{clientName}</h2>
              <span className="text-xs text-slate-400">({items.length})</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {items.map((p) => (
                <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`portal-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{p.label}</p>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 mt-0.5 break-all" data-testid={`portal-url-${p.id}`}>
                        {p.url}<ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    </div>
                    {isStaff && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => openPortal(p)} className="text-slate-400 hover:text-emerald-600" title="Copy credentials & open portal" data-testid={`open-portal-${p.id}`}><LogIn className="w-4 h-4" /></button>
                        {isManager && <button onClick={() => setAudit(p)} className="text-slate-400 hover:text-indigo-600" title="View access log" data-testid={`audit-portal-${p.id}`}><History className="w-4 h-4" /></button>}
                        <button onClick={() => setDialog({ existing: p })} className="text-slate-400 hover:text-indigo-600" data-testid={`edit-portal-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(p)} className="text-slate-400 hover:text-red-500" data-testid={`delete-portal-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                    {!isStaff && (
                      <button onClick={() => openPortal(p)} className="text-slate-400 hover:text-emerald-600 shrink-0" title="Copy credentials & open portal" data-testid={`open-portal-${p.id}`}><LogIn className="w-4 h-4" /></button>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-6 text-xs">
                    <div className="flex items-center gap-1.5">
                      <KeyRound className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-slate-500">User:</span>
                      <span className="font-mono text-slate-700" data-testid={`portal-user-${p.id}`}>{p.username || "—"}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Pass:</span>
                      <Secret value={p.password} testid={`portal-pass-${p.id}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {portals.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl py-16 text-center text-slate-400">
            <KeyRound className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No portal access recorded yet.{isStaff && " Add one so the team can see what's delivered."}
          </div>
        )}
      </div>

      {dialog && <CreatePortalDialog clients={clients} existing={dialog.existing} onClose={() => setDialog(null)} onSaved={load} />}
      {audit && <AuditDialog portal={audit} onClose={() => setAudit(null)} />}
    </div>
  );
}
